import { apiUrl } from "../api/endpoints";
import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from "react";
import axios from "axios";
import moment from "moment";

import DataGridTable from "../Components/DataGridTable";
import Spinner from "../Components/Spinner";
import Alert from "../Components/Alert";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";

import { useAuth } from "../hooks/AuthProvider";
import { AuditLogDetails } from "./AuditLogDetails";

import { LanguageContext } from "../context/LanguageContext";
import { normalizeAuditLogListRow } from "../utils/listRowIds";
import auditTranslations from "../languages/audit.json";
import "./AuditLogs.css";

const countAuditChanges = (row) => {
  const parse = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value;
    try { return JSON.parse(value); } catch (_error) { return null; }
  };
  const oldValues = parse(row?.old_values);
  const newValues = parse(row?.new_values);
  if (Array.isArray(oldValues) || Array.isArray(newValues)) {
    return Math.max(oldValues?.length || 0, newValues?.length || 0);
  }
  if (!oldValues && !newValues) return 0;
  return new Set([...Object.keys(oldValues || {}), ...Object.keys(newValues || {})]).size;
};

const AUDIT_FILTER_FIELDS = [
  { value: "user_name", labelKey: "userName", type: "text" },
  { value: "user_email", labelKey: "userEmail", type: "text" },
  { value: "action", labelKey: "action", type: "text" },
  { value: "type", labelKey: "type", type: "text" },
  { value: "status", labelKey: "status", type: "status" },
  { value: "created_at", labelKey: "createdAt", type: "date" },
];

const TEXT_OPERATORS = [
  { value: "contains", label: "Contains" },
  { value: "equals", label: "Equals" },
  { value: "startsWith", label: "Starts with" },
  { value: "endsWith", label: "Ends with" },
];

const DATE_OPERATORS = [
  { value: "onOrAfter", label: "On or after" },
  { value: "onOrBefore", label: "On or before" },
  { value: "is", label: "On date" },
];

const STATUS_OPTIONS = ["success", "failed", "error", "pending"];

const createAuditFilterDraft = () => ({
  id: "",
  field: "user_name",
  operator: "contains",
  value: "",
});

const getFilterFieldMeta = (field) =>
  AUDIT_FILTER_FIELDS.find((item) => item.value === field) || AUDIT_FILTER_FIELDS[0];

const normalizeAuditFilterForServer = (filter) => {
  const meta = getFilterFieldMeta(filter?.field);
  const value = String(filter?.value || "").trim();
  if (!value) return null;

  let serverValue = value;
  if (meta.type === "date") {
    const timeSuffix =
      filter.operator === "onOrBefore" ? "T23:59:59.999" : "T00:00:00";
    const date = new Date(`${value}${timeSuffix}`);
    if (Number.isNaN(date.getTime())) return null;
    serverValue = date.toISOString();
  }

  return {
    id: filter.id,
    field: filter.field,
    operator: filter.operator,
    value: serverValue,
  };
};

const buildAuditFilterItems = (filters) =>
  (Array.isArray(filters) ? filters : [])
    .map(normalizeAuditFilterForServer)
    .filter(Boolean);

const getOperatorOptionsForField = (field) => {
  const meta = getFilterFieldMeta(field);
  if (meta.type === "date") return DATE_OPERATORS;
  if (meta.type === "status") return [{ value: "equals", label: "Equals" }];
  return TEXT_OPERATORS;
};

const getDefaultOperatorForField = (field) =>
  getOperatorOptionsForField(field)[0]?.value || "contains";

const formatAuditFilterValue = (filter, t) => {
  const meta = getFilterFieldMeta(filter?.field);
  if (meta.type === "status") {
    const key = String(filter?.value || "").toLowerCase();
    return t.statuses?.[key] || filter?.value || "";
  }
  if (meta.type === "date" && filter?.value) {
    return moment(filter.value).format("DD/MM/YYYY");
  }
  return filter?.value || "";
};

const getAuditFieldLabel = (field, t) => {
  const meta = getFilterFieldMeta(field);
  return t.columns?.[meta.labelKey] || meta.labelKey || field;
};

const getAuditOperatorLabel = (operator, field) =>
  getOperatorOptionsForField(field).find((item) => item.value === operator)?.label ||
  operator;

export const AuditLogs = () => {
  const { permissions } = useAuth();
  const { lang } = useContext(LanguageContext);
  const t = auditTranslations[lang]?.auditLogs || auditTranslations.en.auditLogs;

  const canView = permissions["view_audit_logs"];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState([
    { field: "created_at", sort: "desc" },
  ]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [auditFilters, setAuditFilters] = useState([]);
  const [filterDraft, setFilterDraft] = useState(createAuditFilterDraft);
  const [filterEditorOpen, setFilterEditorOpen] = useState(false);
  const [editingFilterId, setEditingFilterId] = useState(null);

  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    type: "info",
    position: ["ar", "ur"].includes(lang) ? "left" : "right",
  });

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const showAlert = useCallback(
    (type, message) => {
      setAlertState({
        open: true,
        type,
        message,
        position: ["ar", "ur"].includes(lang) ? "left" : "right",
      });
    },
    [lang]
  );

  const pagingoptions = [10, 20, 50, 100];
  const effectiveFilterModel = useMemo(() => {
    const gridItems = (filterModel?.items || []).filter(
      (item) => !String(item?.id || "").startsWith("audit-filter-")
    );
    return {
      ...(filterModel || {}),
      logicOperator: "and",
      items: [...gridItems, ...buildAuditFilterItems(auditFilters)],
    };
  }, [auditFilters, filterModel]);

  const hasActiveFilters = (effectiveFilterModel?.items || []).some((item) => {
    if (!item) return false;
    const operator = String(item.operator || "").toLowerCase();
    if (operator === "isempty" || operator === "isnotempty") return true;
    return item.value !== undefined && item.value !== null && String(item.value).trim() !== "";
  });
  const shouldShowGrid = rowCount > 0 || rows.length > 0 || hasActiveFilters;

  const columns = [
    {
      field: "user_name",
      headerName: t.columns.userName,
      width: 180,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "user_email",
      headerName: t.columns.userEmail,
      width: 240,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "action",
      headerName: t.columns.action,
      width: 140,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "type",
      headerName: t.columns.type,
      width: 220,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "status",
      headerName: t.columns.status,
      width: 110,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params) => {
        const value = String(params || "").trim().toLowerCase();
        return t.statuses[value] || params || "";
      },
    },
    {
      field: "changes",
      headerName: t.columns.changes || "Changes",
      width: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      valueGetter: (_value, row) => countAuditChanges(row),
    },
    {
      field: "created_at",
      headerName: t.columns.createdAt,
      width: 220,
      align: "center",
      headerAlign: "center",
      type: "dateTime",
      valueGetter: (params) => (params ? new Date(params) : null),
      valueFormatter: (params) =>
        params ? moment(params).format("DD/MM/YYYY HH:mm:ss") : "",
    },
  ];

  const fetchAuditLogs = useCallback(async () => {
    if (!canView) {
      setRows([]);
      setRowCount(0);
      return;
    }

    setLoading(true);
    try {
      const activeSort =
        sortModel && sortModel.length > 0 ? sortModel[0] : null;
      const sortField = activeSort?.field || "created_at";
      const sortOrder = activeSort?.sort || "desc";

      const res = await axios.get(apiUrl(`audit-logs`), {
        params: {
          page,
          pageSize,
          sortField,
          sortOrder,
          filterModel: JSON.stringify(effectiveFilterModel || { items: [] }),
        },
      });

      if (res.data && res.data.status === "OK") {
        const { data, pagination } = res.data;
        setRows(Array.isArray(data) ? data.map(normalizeAuditLogListRow) : []);
        setRowCount(pagination?.total ?? data.length);
      } else {
        throw new Error(t.invalidResponse);
      }
    } catch (err) {
      console.error("Fetch audit logs error:", err);
      const message =
        err.response?.data?.message ||
        err.message ||
        t.failedLoad;
      showAlert("error", message);
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [canView, effectiveFilterModel, page, pageSize, showAlert, sortModel, t.failedLoad, t.invalidResponse]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const handleRowClick = (params) => {
    setSelectedLog(params.row);
    setDetailsOpen(true);
  };

  const openNewFilterEditor = () => {
    setFilterDraft(createAuditFilterDraft());
    setEditingFilterId(null);
    setFilterEditorOpen(true);
  };

  const openEditFilterEditor = (filter) => {
    setFilterDraft({ ...filter });
    setEditingFilterId(filter.id);
    setFilterEditorOpen(true);
  };

  const closeFilterEditor = () => {
    setFilterDraft(createAuditFilterDraft());
    setEditingFilterId(null);
    setFilterEditorOpen(false);
  };

  const updateFilterDraft = (patch) => {
    setFilterDraft((prev) => {
      const next = { ...prev, ...patch };
      if (patch.field && patch.field !== prev.field) {
        next.operator = getDefaultOperatorForField(patch.field);
        next.value = "";
      }
      return next;
    });
  };

  const applyFilterDraft = () => {
    const normalized = normalizeAuditFilterForServer(filterDraft);
    if (!normalized) return;

    const nextFilter = {
      ...filterDraft,
      id:
        editingFilterId ||
        `audit-filter-${filterDraft.field}-${Date.now()}-${Math.round(Math.random() * 1000)}`,
      value: String(filterDraft.value || "").trim(),
    };

    setAuditFilters((prev) => {
      if (!editingFilterId) return [...prev, nextFilter];
      return prev.map((item) => (item.id === editingFilterId ? nextFilter : item));
    });
    setPage(1);
    closeFilterEditor();
  };

  const removeAuditFilter = (filterId) => {
    setAuditFilters((prev) => prev.filter((item) => item.id !== filterId));
    setPage(1);
    if (editingFilterId === filterId) closeFilterEditor();
  };

  const resetAuditFilters = () => {
    setAuditFilters([]);
    setFilterModel({ items: [] });
    setPage(1);
    closeFilterEditor();
  };

  const auditFilterToolbar = (
    <div className="audit-toolbar-filter">
      <div className="audit-filter-chip-row">
        {auditFilters.map((filter) => (
          <button
            key={filter.id}
            type="button"
            className="audit-filter-chip"
            onClick={() => openEditFilterEditor(filter)}
            title={`${getAuditFieldLabel(filter.field, t)} ${getAuditOperatorLabel(filter.operator, filter.field)} ${formatAuditFilterValue(filter, t)}`}
          >
            <FilterListIcon fontSize="inherit" />
            <span className="audit-filter-chip__field">
              {getAuditFieldLabel(filter.field, t)}
            </span>
            <span className="audit-filter-chip__operator">
              {getAuditOperatorLabel(filter.operator, filter.field)}
            </span>
            <strong>{formatAuditFilterValue(filter, t)}</strong>
            <span
              role="button"
              tabIndex={0}
              className="audit-filter-chip__remove"
              onClick={(event) => {
                event.stopPropagation();
                removeAuditFilter(filter.id);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  event.stopPropagation();
                  removeAuditFilter(filter.id);
                }
              }}
              aria-label="Remove filter"
            >
              <CloseIcon fontSize="inherit" />
            </span>
          </button>
        ))}

        <button
          type="button"
          className="audit-filter-add-btn"
          onClick={filterEditorOpen ? closeFilterEditor : openNewFilterEditor}
        >
          {filterEditorOpen ? <CloseIcon fontSize="small" /> : <AddIcon fontSize="small" />}
          <span>{filterEditorOpen ? "Close" : "Add filter"}</span>
        </button>

        {auditFilters.length > 0 ? (
          <button type="button" className="audit-filter-clear-btn" onClick={resetAuditFilters}>
            Clear
          </button>
        ) : null}
      </div>

      {filterEditorOpen ? (
        <div className="audit-filter-editor">
          <select
            className="audit-filter-control audit-filter-control--field"
            value={filterDraft.field}
            onChange={(event) => updateFilterDraft({ field: event.target.value })}
            aria-label="Filter field"
          >
            {AUDIT_FILTER_FIELDS.map((field) => (
              <option key={field.value} value={field.value}>
                {t.columns?.[field.labelKey] || field.labelKey}
              </option>
            ))}
          </select>

          <select
            className="audit-filter-control audit-filter-control--operator"
            value={filterDraft.operator}
            onChange={(event) => updateFilterDraft({ operator: event.target.value })}
            aria-label="Filter operator"
          >
            {getOperatorOptionsForField(filterDraft.field).map((operator) => (
              <option key={operator.value} value={operator.value}>
                {operator.label}
              </option>
            ))}
          </select>

          {getFilterFieldMeta(filterDraft.field).type === "status" ? (
            <select
              className="audit-filter-control audit-filter-control--value"
              value={filterDraft.value}
              onChange={(event) => updateFilterDraft({ value: event.target.value })}
              aria-label="Filter value"
            >
              <option value="">Status</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {t.statuses?.[status] || status}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="audit-filter-control audit-filter-control--value"
              type={getFilterFieldMeta(filterDraft.field).type === "date" ? "date" : "text"}
              value={filterDraft.value}
              onChange={(event) => updateFilterDraft({ value: event.target.value })}
              onKeyDown={(event) => {
                if (event.key === "Enter") applyFilterDraft();
                if (event.key === "Escape") closeFilterEditor();
              }}
              placeholder="Filter value"
              aria-label="Filter value"
            />
          )}

          <button
            type="button"
            className="audit-filter-apply-btn"
            onClick={applyFilterDraft}
            disabled={!normalizeAuditFilterForServer(filterDraft)}
          >
            <CheckIcon fontSize="small" />
            <span>{editingFilterId ? "Update" : "Apply"}</span>
          </button>
        </div>
      ) : null}
    </div>
  );

  if (!canView) {
    return (
      <div
        style={{
          padding: "24px",
          color: "var(--cell-color)",
        }}
      >
        {t.permissionDenied}
      </div>
    );
  }

  return (
    <div
      className="configuration devices-page"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Alert
        open={alertState.open}
        close={() =>
          setAlertState((prev) => ({ ...prev, open: false }))
        }
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div
        className="device-main"

      >
        <div className="devices-header">
          <div className="devices-header-left">
            <h2
              className="devices-title"
            >
              {t.title}
            </h2>
          </div>
        </div>

        {loading && !shouldShowGrid ? (
          <Spinner />
        ) : shouldShowGrid ? (
          <div
            className="devices-grid-wrapper"
            style={{
              flex: 1,
              minHeight: 0,
              marginTop: "20px",
            }}
          >
            <DataGridTable
              columns={columns}
              rows={rows}
              idcolumn={"audit_log_id"}
              pagingoptions={pagingoptions}
              sortField="created_at"
              sortType="desc"
              handleRowClick={handleRowClick}
              checkboxSelection={false}
              rowSelectionModel={[]}
              setRowSelectionModel={() => {}}
              selectActions={[]}
              showToolbar={true}
              tableKey="audit_logs"
              loading={loading}
              toolbarSuffix={auditFilterToolbar}
              pagination={{
                serverSide: true,
                page,
                pageSize,
                count: rowCount,
                sortModel,
                onPageChange: (newPage) => setPage(newPage),
                onPageSizeChange: (newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                },
                onSortModelChange: (newModel) => {
                  setSortModel(
                    newModel && newModel.length ? newModel : []
                  );
                  setPage(1);
                },
                onFilterModelChange: (newModel) => {
                  setFilterModel(newModel || { items: [] });
                  setPage(1);
                },
              }}
            />
          </div>
        ) : (
          <div
            style={{
              width: "100%",
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize:
                "calc(var(--body-font-size) * var(--font-size-multiplier))",
            }}
          >
            {t.empty}
          </div>
        )}
      </div>

      <AuditLogDetails
        open={detailsOpen}
        log={selectedLog}
        onClose={() => setDetailsOpen(false)}
      />
    </div>
  );
};

export default AuditLogs;
